import { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { lambdaOperationsApi, LambdaHistoryItem } from '@api/lambdaOperationsApi';
import { isErrorResponse } from '@utils/utils';
import { useToastContext } from '@context/toastContext';
import { useTranslation } from 'react-i18next';
import { useTypedSelector } from '@redux/Hooks/storeSelectors';
import { selectAllLayersIdsBySourceId, selectSourcesById } from '@redux/selectors/selectors';
import {
    addedSourceData,
    addedLayers,
    addedSources,
    changedLayerVisibility,
    setLayerLoading,
} from '@redux/map/map-reducer';
import { entityMapLayersToSourcesAndLayers } from '@utils/mapUtils';
import { normalizeLambdaLayers } from '../lambdaOperations.utils';

type SourceIdsByLambdaId = Record<string, string[]>;

export const useLambdaLayerToggle = (contextKey?: string) => {
    const [checkedById, setCheckedById] = useState<Record<string, boolean>>({});
    const [loadingById, setLoadingById] = useState<Record<string, boolean>>({});
    const [sourceIdsByLambdaId, setSourceIdsByLambdaId] = useState<SourceIdsByLambdaId>({});
    const sourceIdsByLambdaIdRef = useRef<SourceIdsByLambdaId>({});
    const layerIdsBySourceIdRef = useRef<Record<string, string[]>>({});
    const sourcesById = useTypedSelector(selectSourcesById);
    const layerIdsBySourceId = useTypedSelector(selectAllLayersIdsBySourceId);
    const dispatch = useDispatch();
    const { addToast } = useToastContext();
    const { t } = useTranslation();

    const setLayerIdsVisibility = useCallback(
        (layerIds: string[], visible: boolean) => {
            layerIds.forEach(layerId => {
                dispatch(changedLayerVisibility({ layerId, visible }));
            });
        },
        [dispatch]
    );

    const setSourcesVisibility = useCallback(
        (sourceIds: string[], visible: boolean) => {
            sourceIds.forEach(sourceId => {
                const layerIds = layerIdsBySourceId[sourceId] || [sourceId];
                setLayerIdsVisibility(layerIds, visible);
            });
        },
        [layerIdsBySourceId, setLayerIdsVisibility]
    );

    useEffect(() => {
        sourceIdsByLambdaIdRef.current = sourceIdsByLambdaId;
    }, [sourceIdsByLambdaId]);

    useEffect(() => {
        layerIdsBySourceIdRef.current = layerIdsBySourceId;
    }, [layerIdsBySourceId]);

    useEffect(
        () => () => {
            Object.values(sourceIdsByLambdaIdRef.current).forEach(sourceIds => {
                sourceIds.forEach(sourceId => {
                    const layerIds = layerIdsBySourceIdRef.current[sourceId] || [sourceId];
                    setLayerIdsVisibility(layerIds, false);
                });
            });
        },
        [contextKey, setLayerIdsVisibility]
    );

    useEffect(() => {
        Object.entries(checkedById).forEach(([lambdaId, checked]) => {
            if (checked) {
                setSourcesVisibility(sourceIdsByLambdaId[lambdaId] || [], true);
            }
        });
    }, [checkedById, layerIdsBySourceId, setSourcesVisibility, sourceIdsByLambdaId]);

    const toggleLayer = useCallback(
        async (item: LambdaHistoryItem, checked: boolean) => {
            const lambdaId = item.id;
            const knownSourceIds = sourceIdsByLambdaId[lambdaId] || [];

            if (!checked) {
                setSourcesVisibility(knownSourceIds, false);
                setCheckedById(prev => ({ ...prev, [lambdaId]: false }));
                return;
            }

            setLoadingById(prev => ({ ...prev, [lambdaId]: true }));

            try {
                if (knownSourceIds.length) {
                    setSourcesVisibility(knownSourceIds, true);
                    setCheckedById(prev => ({ ...prev, [lambdaId]: true }));
                    return;
                }

                const abortController = new AbortController();
                const resp = await lambdaOperationsApi.getLambdaLayer({
                    lambdaId,
                    item,
                    abortSignal: abortController.signal,
                });

                if (isErrorResponse(resp)) {
                    addToast(resp.error_description || t('lambda-operations.layer-load-error'), 'error');
                    setCheckedById(prev => ({ ...prev, [lambdaId]: false }));
                    return;
                }

                const entityMapLayers = normalizeLambdaLayers(resp, item);
                const sourceIds = entityMapLayers.map(layer => layer.id);
                const layersToAdd = entityMapLayers.filter(layer => !sourcesById[layer.id]);
                const layersWithData = entityMapLayers.filter(layer => layer.featureCollection);
                let addedLayerIds: string[] = [];

                if (layersToAdd.length) {
                    const { sources, layers } = entityMapLayersToSourcesAndLayers([
                        {
                            entityMapLayers: layersToAdd,
                            alwaysLocked: true,
                            isLockBtnVisible: false,
                        },
                    ]);
                    addedLayerIds = layers.map(layer => layer.id);
                    dispatch(addedSources(sources));
                    dispatch(addedLayers({ layers, position: 'top' }));
                }

                layersWithData.forEach(layer => {
                    if (!layer.featureCollection) {
                        return;
                    }

                    dispatch(
                        addedSourceData({
                            sourceId: layer.id,
                            data: layer.featureCollection,
                            ...(layer.legend ? { legend: layer.legend } : {}),
                            ...(layer.distribution
                                ? {
                                      distribution: {
                                          ...layer.distribution,
                                          layerName: layer.displayName,
                                      },
                                  }
                                : {}),
                        })
                    );
                    dispatch(setLayerLoading({ layerId: layer.id, loading: false, loaded: true }));
                });

                sourceIds.forEach(sourceId => {
                    dispatch(setLayerLoading({ layerId: sourceId, loading: false, loaded: false }));
                });

                setSourceIdsByLambdaId(prev => ({ ...prev, [lambdaId]: sourceIds }));
                setLayerIdsVisibility(addedLayerIds, true);
                setSourcesVisibility(sourceIds, true);
                setCheckedById(prev => ({ ...prev, [lambdaId]: true }));
            } finally {
                setLoadingById(prev => ({ ...prev, [lambdaId]: false }));
            }
        },
        [
            addToast,
            dispatch,
            setLayerIdsVisibility,
            setSourcesVisibility,
            sourceIdsByLambdaId,
            sourcesById,
            t,
        ]
    );

    return { checkedById, loadingById, toggleLayer };
};
